/* ============================================================
   UTM capture + propagation.
   When a visitor lands with ?utm_source=chatgpt&utm_campaign=… we stash those
   for the whole session and append them to outbound links (banners, sponsor).
   This lets a ChatGPT → Calculator → e-commerce chain carry attribution.
   NOTE: the destination site must actually log UTMs at checkout for a sale to
   be attributed — we only pass them through.
   ============================================================ */
const KEY = 'pc:utm'
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']

// Read any UTM (and common click-id) params off the landing URL and store them
// for the session. Call once on app load.
export function captureUtms() {
  try {
    const params = new URLSearchParams(window.location.search)
    const found = {}
    for (const k of UTM_KEYS) {
      const v = params.get(k)
      if (v) found[k] = v
    }
    for (const id of ['gclid', 'fbclid', 'ref']) {
      const v = params.get(id)
      if (v) found[id] = v
    }
    if (Object.keys(found).length) sessionStorage.setItem(KEY, JSON.stringify(found))
  } catch {
    /* ignore */
  }
}

export function getUtms() {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || '{}')
  } catch {
    return {}
  }
}

// Capture the first-touch referrer + landing page for the session (once). Stored
// alongside UTMs so a signup can record where the user actually came from —
// powers Slack signup alerts and the admin signup stats.
const VISIT_KEY = 'pc:visit'
export function captureVisit() {
  try {
    if (sessionStorage.getItem(VISIT_KEY)) return
    sessionStorage.setItem(VISIT_KEY, JSON.stringify({
      referrer: document.referrer || '',
      landing: window.location.pathname || '/',
    }))
  } catch {
    /* ignore */
  }
}

export function getVisit() {
  try {
    return JSON.parse(sessionStorage.getItem(VISIT_KEY) || '{}')
  } catch {
    return {}
  }
}

// Mobile vs desktop from the UA (coarse, but enough for a signup breakdown).
export function deviceType() {
  try {
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
  } catch {
    return 'unknown'
  }
}

// Parse a "utm_source=x&utm_content=y" string into an object.
export function parseQuery(qs) {
  const out = {}
  if (!qs) return out
  try {
    new URLSearchParams(qs.replace(/^\?/, '')).forEach((v, k) => { out[k] = v })
  } catch {
    /* ignore */
  }
  return out
}

// Add params to a URL without overriding ones already present.
export function appendParams(url, params) {
  if (!url) return url
  try {
    const u = new URL(url, window.location.origin)
    for (const [k, v] of Object.entries(params || {})) {
      if (v && !u.searchParams.has(k)) u.searchParams.set(k, v)
    }
    return u.toString()
  } catch {
    return url
  }
}

// Append the session's captured UTMs to an outbound URL.
export function withUtm(url) {
  return appendParams(url, getUtms())
}

// Build a banner's outbound URL: its own UTM tags first, then session UTMs.
export function bannerHref(banner) {
  if (!banner?.link_url) return null
  const withOwn = appendParams(banner.link_url, parseQuery(banner.utm))
  return withUtm(withOwn)
}
